import React, { useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ArrowLeft, ArrowRight, CheckCircle2, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PROPOSAL_TYPES = [
  { id: "immobile_da_ristrutturare", title: "Immobile da ristrutturare", desc: "Appartamento, ufficio, casa maltenuta" },
  { id: "palazzo_fabbricato", title: "Palazzo o fabbricato intero", desc: "Edificio da riqualificare o valorizzare" },
  { id: "terreno_edificabile", title: "Terreno edificabile", desc: "Area con potenzialità edificatorie" },
  { id: "demolizione_ricostruzione", title: "Immobile da demolire e ricostruire", desc: "Edificio da abbattere e sostituire" },
];

const AGREEMENTS = [
  { id: "vendita_diretta", title: "Vendita diretta", desc: "Vuoi venderci l'immobile" },
  { id: "partnership", title: "Partnership", desc: "Ristrutturiamo e rivendiamo insieme" },
  { id: "permuta", title: "Permuta", desc: "Cedi l'immobile, ricevi unità nel nuovo progetto" },
  { id: "valutazione", title: "Voglio una valutazione", desc: "Non lo so ancora, aiutatemi a scegliere" },
];

const STEPS = ["Cosa proponi", "Che accordo", "Immobile", "Foto", "Contatti"];

export default function ProponiWizard() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    proposal_type: "",
    agreement_type: "",
    location: "",
    sqm: "",
    state_description: "",
    notes: "",
    name: "",
    email: "",
    privacy_consent: false,
    image_paths: [],
    // Honeypot antibot: nascosto, se compilato il backend scarta la richiesta.
    website: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef();

  const progress = ((step + 1) / STEPS.length) * 100;

  const canNext = () => {
    if (step === 0) return !!data.proposal_type;
    if (step === 1) return !!data.agreement_type;
    if (step === 2) return !!data.location;
    if (step === 3) return true;
    if (step === 4) return data.name && data.email && data.privacy_consent;
    return true;
  };

  const handleUpload = async (files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/uploads/proposal", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setData((d) => ({ ...d, image_paths: [...d.image_paths, res.data.path] }));
      }
      toast.success("Foto caricate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setUploading(false);
    }
  };

  const removeImg = (p) => {
    setData((d) => ({ ...d, image_paths: d.image_paths.filter((x) => x !== p) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.privacy_consent) {
      toast.error("Devi accettare la privacy policy");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/proposals", {
        ...data,
        sqm: data.sqm ? parseInt(data.sqm) : null,
      });
      // Evento GA4: conversione principale
      track("proposta_inviata", { tipo_proposta: data.proposal_type });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="risco-card p-10 text-center" data-testid="proponi-success">
        <CheckCircle2 className="w-16 h-16 text-risco-accent mx-auto" />
        <h3 className="font-heading text-3xl text-risco-ink mt-6">Proposta ricevuta!</h3>
        <p className="text-risco-muted mt-4 max-w-md mx-auto">
          Grazie! Il nostro team analizzerà la tua proposta e ti ricontatterà entro 48 ore lavorative con una prima valutazione.
        </p>
      </div>
    );
  }

  return (
    <div className="risco-card p-6 md:p-10" data-testid="proponi-wizard">
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs text-risco-muted mb-2">
          <div>Step {step + 1} / {STEPS.length}</div>
          <div>{STEPS[step]}</div>
        </div>
        <div className="h-1.5 bg-risco-alt rounded-full overflow-hidden">
          <div className="h-full bg-risco-accent transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {step === 0 && (
        <div data-testid="prop-step-tipo">
          <h3 className="font-heading text-2xl text-risco-ink">Cosa vuoi proporre?</h3>
          <div className="grid md:grid-cols-2 gap-3 mt-6">
            {PROPOSAL_TYPES.map((p) => (
              <button key={p.id} type="button" onClick={() => setData({ ...data, proposal_type: p.id })} data-testid={`prop-type-${p.id}`}
                className={`p-5 rounded-lg border text-left transition-colors ${
                  data.proposal_type === p.id ? "border-risco-accent bg-risco-accent-soft" : "border-stone-300 hover:border-stone-500 bg-white"
                }`}>
                <div className="font-heading text-lg text-risco-ink">{p.title}</div>
                <div className="text-sm text-risco-muted mt-1">{p.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 1 && (
        <div data-testid="prop-step-accordo">
          <h3 className="font-heading text-2xl text-risco-ink">Che tipo di accordo ti interessa?</h3>
          <div className="grid md:grid-cols-2 gap-3 mt-6">
            {AGREEMENTS.map((a) => (
              <button key={a.id} type="button" onClick={() => setData({ ...data, agreement_type: a.id })} data-testid={`prop-agreement-${a.id}`}
                className={`p-5 rounded-lg border text-left transition-colors ${
                  data.agreement_type === a.id ? "border-risco-accent bg-risco-accent-soft" : "border-stone-300 hover:border-stone-500 bg-white"
                }`}>
                <div className="font-heading text-lg text-risco-ink">{a.title}</div>
                <div className="text-sm text-risco-muted mt-1">{a.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="prop-step-immobile">
          <h3 className="font-heading text-2xl text-risco-ink">Parlaci dell'immobile</h3>
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="md:col-span-2">
              <label className="text-sm text-stone-700">Indirizzo o zona *</label>
              <input value={data.location} onChange={(e) => setData({ ...data, location: e.target.value })} data-testid="prop-input-location" placeholder="es. Napoli, Vomero" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
            </div>
            <div>
              <label className="text-sm text-stone-700">Superficie (mq)</label>
              <input type="number" value={data.sqm} onChange={(e) => setData({ ...data, sqm: e.target.value })} data-testid="prop-input-sqm" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
            </div>
            <div>
              <label className="text-sm text-stone-700">Stato attuale (breve)</label>
              <input value={data.state_description} onChange={(e) => setData({ ...data, state_description: e.target.value })} data-testid="prop-input-state" placeholder="es. Da ristrutturare, ultimo piano" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-stone-700">Note aggiuntive</label>
              <textarea rows={4} value={data.notes} onChange={(e) => setData({ ...data, notes: e.target.value })} data-testid="prop-input-notes" placeholder="Qualsiasi dettaglio utile..." className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div data-testid="prop-step-foto">
          <h3 className="font-heading text-2xl text-risco-ink">Aggiungi foto (facoltativo)</h3>
          <p className="text-risco-muted mt-2">Max 8MB per foto. Formati: JPG, PNG, WEBP.</p>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => handleUpload(e.target.files)} data-testid="prop-file-input" className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="prop-upload-btn"
            className="mt-6 w-full py-8 rounded-lg border-2 border-dashed border-stone-300 hover:border-risco-accent flex flex-col items-center justify-center gap-2 text-stone-700 transition-colors">
            {uploading ? <><Loader2 className="w-6 h-6 animate-spin" /> Caricamento...</> : <><Upload className="w-6 h-6" /> Clicca per caricare le foto</>}
          </button>
          {data.image_paths.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-6">
              {data.image_paths.map((p) => (
                <div key={p} className="relative group aspect-square">
                  <img src={fileUrl(p)} alt="upload" className="w-full h-full object-cover rounded-md border border-risco-border" />
                  <button type="button" onClick={() => removeImg(p)} data-testid={`prop-remove-${p.split('/').pop()}`} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-risco-dark/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3 text-risco-ink" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <form onSubmit={submit} data-testid="prop-step-contatti">
          <h3 className="font-heading text-2xl text-risco-ink">I tuoi contatti</h3>
          <p className="text-risco-muted mt-2">Ti ricontatteremo entro 48 ore.</p>
          <div className="grid md:grid-cols-1 gap-4 mt-6">
            <div>
              <label className="text-sm text-stone-700">Nome e cognome *</label>
              <input required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} data-testid="prop-input-name" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
            </div>
            <div>
              <label className="text-sm text-stone-700">Email *</label>
              <input required type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} data-testid="prop-input-email" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
            </div>
          </div>
          <label className="flex items-start gap-2 mt-5 text-sm text-stone-700">
            <input type="checkbox" checked={data.privacy_consent} onChange={(e) => setData({ ...data, privacy_consent: e.target.checked })} data-testid="prop-privacy" className="mt-1 accent-risco-accent" />
            Ho letto l'informativa Privacy e acconsento al trattamento dei dati.
          </label>
          <button type="submit" disabled={submitting || !canNext()} data-testid="prop-submit" className="mt-6 w-full py-3 rounded-md bg-risco-accent hover:bg-risco-accent-dark disabled:opacity-50 text-white font-semibold">
            {submitting ? "Invio..." : "Invia la tua proposta"}
          </button>
          {/* Honeypot antibot: nascosto lato UI, se compilato il backend scarta la richiesta. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
            <label htmlFor="prop-hp-website">Sito web (non compilare)</label>
            <input
              type="text"
              id="prop-hp-website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={data.website}
              onChange={(e) => setData({ ...data, website: e.target.value })}
            />
          </div>
        </form>
      )}

      <div className="flex items-center justify-between mt-10 pt-6 border-t border-risco-border">
        <button type="button" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="prop-back" className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-stone-300 text-stone-700 disabled:opacity-30 disabled:cursor-not-allowed">
          <ArrowLeft className="w-4 h-4" /> Indietro
        </button>
        {step < STEPS.length - 1 && (
          <button type="button" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} data-testid="prop-next" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-risco-accent hover:bg-risco-accent-dark disabled:opacity-40 text-white font-semibold">
            Avanti <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
